<template>
    <div class="general-modal">
        <BaseModal :padding="isMobileOrTablet ? MODAL_PADDING_MOBILE : MODAL_PADDING_DESKTOP"
                   maxWidth="500px"
                   maxHeight="500px"
                   useContentFullHeight
                   useClickOutsideClose
                   @close="handleClose">
            <template #header/>
            <template #content>
                <div class="general-modal__content general-modal__content--justify-center">
                    <AnimatedBits v-if="bybitDealRewardClaimedNotification?.data?.reward_amount?.amount"
                                  :amount="bybitDealRewardClaimedNotification?.data?.reward_amount?.amount"
                                  :currency="bybitDealRewardClaimedNotification?.data?.reward_amount?.currency"/>
                    <div class="general-modal__title">
                        {{ bybitDealRewardClaimedNotification?.data?.title }}
                    </div>
                    <div class="general-modal__subtitle">
                        {{ bybitDealRewardClaimedNotification?.data?.subtitle }}
                    </div>
                </div>
                <div class="general-modal__confetti">
                    <ConfettiExplosion v-bind="getConfettiProps" />
                </div>
            </template>
            <template #footer>
                <div class="general-modal__actions">
                    <span class="general-modal__action general-modal__action--confirm">
                        <BaseButton v-focus
                                    @click="handleClose">
                            <Translate translationKey="quests-dashboard.earnMore"
                                       fallback="Earn More" /> 🔥
                        </BaseButton>
                    </span>
                </div>
            </template>
        </BaseModal>
    </div>
</template>

<script setup lang="ts">
    import ConfettiExplosion from 'vue-confetti-explosion';
    import {
        computed, ref, watch,
    } from 'vue';
    import BaseModal from '@/js/components/BaseModal.vue';
    import { ModalEnum, useModalStore } from '@/js/store/quests/game/modules/ModalStore';
    import { useModalActive } from '@/js/composables/useModalActive';
    import { useDeviceSize } from '@/js/composables/useDeviceSize';
    import BaseButton from '@/js/components/BaseButton.vue';
    import { NotificationModalTypeEnum } from '@/js/quests/dashboard/types/enums';
    import { Notification } from '@/js/quests/dashboard/types/types';
    import { useUserStore } from '@/js/store/quests/dashboard/UserStore';
    import AnimatedBits from '@/js/quests/dashboard/modals/notifications/AnimatedBits.vue';
    import { MODAL_PADDING_DESKTOP, MODAL_PADDING_MOBILE } from '@/js/quests/global/constants/modal';

    const modalStore = useModalStore();
    const userStore = useUserStore();
    const { isMobileOrTablet } = useDeviceSize();

    const notifications = ref<Notification[]>([]);
    const bybitDealRewardClaimedNotification = ref();

    const getConfettiProps = computed(() => ({
        duration: 2000,
    }) as unknown);

    const handleClose = () => {
        modalStore.toggleModal(ModalEnum.BYBIT_DEAL_REWARD_CLAIMED, false);
    };

    useModalActive();

    watch(
        () => modalStore.modals[ModalEnum.BYBIT_DEAL_REWARD_CLAIMED],
        (newValue) => {
            if (newValue) {
                notifications.value = userStore?.notifications || [];
                const filteredNotifications = notifications.value.filter(
                    (notification) => notification.type === NotificationModalTypeEnum.BybitDealRewardClaimed,
                );
                bybitDealRewardClaimedNotification.value = filteredNotifications.length > 0 ? filteredNotifications[0] : null;
            }
        },
        { immediate: true },
    );
</script>

<style scoped lang="scss">
@use "@/js/quests/dashboard/sass/mixins/_modal" as modalMixins;

.general-modal {
    @include modalMixins.modal;

    &__confetti {
        top: 50%;
    }
}
</style>
